import { useRef, forwardRef, useImperativeHandle, useCallback } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import MenuListBase, * as MenuList from '@segment/matcha/components/MenuList'
import SearchBar from '@segment/matcha/components/SearchBar'

import useAutocomplete from '@segment/utils/useAutocomplete'
import type { StateBaseItem } from '@segment/utils/useAutocomplete'

type AutocompleteRef = HTMLInputElement | null
type MarkVariant = `title` | `content`

const APP_ID = process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID_HELP ?? ``
const API_KEY = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY_HELP ?? ``
const INDEX_NAME = process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME_HELP ?? ``

const SOURCE_ID = `products`
const PLACEHOLDER_TEXT = `Search the knowledge base...`

export interface AutocompleteStateHelpPageProps extends StateBaseItem {
  objectID: string
  title: string
  content: string
  query: string
  anchor?: string
  html: string
  headings: string[]
  strat: string
  last_modified_at: string
  tags: string[]
  categories: string[]
  excerpt_html: string
  excerpt_text: string
  slug: string
  type: string
  url: string
}

interface AutocompleteProps {
  /**
   * It changes the name of the form control.
   */
  name: string
}
const Autocomplete = forwardRef<AutocompleteRef, AutocompleteProps>(({ name }, ref) => {
  const inputRef = useRef<AutocompleteRef>(null)
  useImperativeHandle<AutocompleteRef, AutocompleteRef>(ref, () => inputRef.current)

  const { autocompleteState, autocomplete } = useAutocomplete<AutocompleteStateHelpPageProps>({
    insightsInitParams: { appId: APP_ID, apiKey: API_KEY },
    appId: APP_ID,
    apiKey: API_KEY,
    indexName: INDEX_NAME,
    sourceId: SOURCE_ID,
    placeholder: PLACEHOLDER_TEXT,
  })

  const highlight = useCallback(
    (entry: string, variant: MarkVariant) => {
      if (autocompleteState?.query) {
        const parts = entry?.split(new RegExp(String.raw`(${autocompleteState.query})`, `gi`))

        return parts?.map((part, i) => {
          if (i % 2 === 0) {
            return part
          }

          return (
            <Mark role="mark" key={`${entry}-${i}`} variant={variant}>
              {part}
            </Mark>
          )
        })
      }
    },
    [autocompleteState]
  )

  return (
    <Wrapper {...autocomplete.getRootProps({})}>
      <SearchBar
        name={name}
        ref={inputRef}
        aria-label={PLACEHOLDER_TEXT}
        {...autocomplete.getInputProps({ inputElement: inputRef.current })}
        onClear={() => {
          autocomplete.setQuery(``)
          autocomplete.refresh()
        }}
      />
      <Panel {...autocomplete.getPanelProps({})}>
        {autocompleteState?.isOpen &&
          autocompleteState.collections.map((collection) => {
            const { source, items } = collection

            return (
              <MenuListWrapper key={collection.source.sourceId}>
                {items.length > 0 && (
                  <StyledMenuListBase interactive={true} {...autocomplete.getListProps()}>
                    {items.map((item) => {
                      const anchorLink = item.anchor ? `#${item.anchor}` : ``
                      return (
                        <Link href={`/docs${item.url}${anchorLink}`} key={item.objectID}>
                          <StyledMenuListItem
                            {...autocomplete.getItemProps({
                              item,
                              source,
                            })}
                          >
                            <>
                              <Title>{highlight(item.title, `title`)}</Title>
                              {` / `}
                              {highlight(item.content, `content`)}
                            </>
                          </StyledMenuListItem>
                        </Link>
                      )
                    })}
                  </StyledMenuListBase>
                )}
              </MenuListWrapper>
            )
          })}
      </Panel>
    </Wrapper>
  )
})

export default Autocomplete

const Wrapper = styled.div`
  position: relative;
`

const Panel = styled.div`
  // Render on top of Input's ErrorWrapper
  position: absolute;
  width: 100%;
  top: 58px;
`

const MenuListWrapper = styled.div``

const StyledMenuListItem = styled(MenuList.Item)`
  text-align: left;
  :hover {
    background: ${({ theme }) => theme.palette.twilio.gray[10]};
  }
`

const Link = styled.a`
  color: inherit;
  text-decoration: none;
`

interface MarkProps {
  variant: MarkVariant
}
const Mark = styled.mark<MarkProps>`
  ${({ variant, theme }) => {
    switch (variant) {
      case `title`:
        return css`
          color: ${theme.palette.twilio.gray[100]};
          background: ${theme.palette.twilio.gray[20]};
        `

      case `content`:
      default:
        return css`
          color: ${theme.palette.twilio.green[60]};
          background: none;
        `
    }
  }}
  font-weight: 600;
`

const Title = styled.span`
  color: ${({ theme }) => theme.palette.twilio.gray[60]};
`

const StyledMenuListBase = styled(MenuListBase)`
  position: absolute;
  z-index: 2;
`
